'use client';

import { useSyncExternalStore } from 'react';
import type { FeatureGateName } from '@/config/feature-gates';
import { checkGate, getProviderReady, subscribeProviderReady } from '@/lib/analytics';
import {
  getGateValuesPending,
  getGateValuesVersion,
  subscribeGateValues,
} from '@/lib/analytics/gate-values';
import {
  getDevToolkitAccess,
  subscribeDevToolkitAccess,
  useDevToolkitStore,
} from '@/stores/dev-toolkit';

/**
 * Resolves a feature gate only after the analytics provider has fresh values.
 * `null` means the provider is still loading — or an identity update is in
 * flight, so the current evaluation is the anonymous user's; callers must
 * preserve pending actions such as deep-link parameters until the value
 * becomes boolean. Re-renders when settled values change (post sign-in).
 */
export function useFeatureGate(name: FeatureGateName): boolean | null {
  const localOverride = useDevToolkitStore((state) => state.overrides[name]);
  const devToolkitAccess = useSyncExternalStore(
    subscribeDevToolkitAccess,
    getDevToolkitAccess,
    (): false => false,
  );
  const providerReady = useSyncExternalStore(
    subscribeProviderReady,
    getProviderReady,
    (): false => false,
  );
  // Subscribed for its change signal only: a bump means the provider's gate
  // values changed and `checkGate` must be re-read.
  useSyncExternalStore(subscribeGateValues, getGateValuesVersion, (): number => 0);
  const identityPending = useSyncExternalStore(
    subscribeGateValues,
    getGateValuesPending,
    (): false => false,
  );
  const hasAccessibleOverride = devToolkitAccess && localOverride !== undefined;
  if ((!providerReady || identityPending) && !hasAccessibleOverride) return null;
  return checkGate(name);
}
